import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { timeAgo } from "@/lib/brand";
import { Bell, AtSign } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";

const TABS = [
  { k: "all", label: "All" },
  { k: "mention", label: "Mentions" },
];

export function NotificationBell() {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all");

  const loadNotifs = useCallback(async () => {
    try {
      const [n, c] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setNotifs(n.data);
      setUnread(c.data.count);
    } catch (e) {
      console.debug("Notification fetch failed", e);
    }
  }, []);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 15000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  const openNotif = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      setUnread((u) => Math.max(0, u - 1));
      setNotifs((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setNotifs((arr) => arr.map((x) => ({ ...x, read: true })));
  };

  const shownNotifs = filter === "mention" ? notifs.filter((n) => n.type === "mention") : notifs;
  const mentionUnread = notifs.filter((n) => n.type === "mention" && !n.read).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative h-10 w-10 rounded-md hover:bg-slate-100 flex items-center justify-center" data-testid="notifications-btn">
          <Bell size={19} className="text-slate-600" />
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2.5 border-b">
          <span className="font-medium text-sm">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-sky-600 hover:underline" data-testid="mark-all-read-btn">
              Mark all read
            </button>
          )}
        </div>
        <div className="flex gap-1 px-2 py-1.5 border-b">
          {TABS.map((tab) => {
            const cnt = tab.k === "mention" ? mentionUnread : unread;
            return (
              <button
                key={tab.k}
                onClick={() => setFilter(tab.k)}
                data-testid={`notif-filter-${tab.k}`}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                  filter === tab.k ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                {tab.label}{cnt > 0 && <span className="ml-1 opacity-80">({cnt})</span>}
              </button>
            );
          })}
        </div>
        <ScrollArea className="h-80">
          {shownNotifs.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-10">
              {filter === "mention" ? "No mentions yet" : "No notifications yet"}
            </p>
          ) : (
            shownNotifs.map((n) => (
              <button
                key={n.id}
                onClick={() => openNotif(n)}
                data-testid="notification-item"
                className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                  n.read ? "" : "bg-sky-50/50"
                }`}
              >
                <p className="text-sm text-slate-800 leading-snug flex items-start gap-1.5">
                  {n.type === "mention" && <AtSign size={13} className="text-sky-600 mt-0.5 shrink-0" />}
                  <span>{n.message}</span>
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
              </button>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
